// src/component/admin/support/NewsletterTab.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  SearchIcon,
  ArrowDown,
  EmailIcon,
  CheckCircleIcon,
  CautionIcon,
  FilterIcon,
  CloseIcon
} from '../../../public/icons';
import Pagination from '../../shared/Pagination';
import adminNewsletterService from '../../../services/admin/admin-newsletterService';
import { useToast } from '../../../context/ToastContext';

/* ─────────── Mobile Filters Modal ─────────── */
const MobileFiltersModal = ({
  isOpen,
  onClose,
  searchQuery,
  setSearchQuery,
  statusFilter,
  setStatusFilter,
  statusFilters
}) => {
  if (!isOpen) return null;
  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={onClose} />
      <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl z-50 p-5 max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-lg font-medium text-[#1C2A46]">Filters</h3>
          <button onClick={onClose} className="p-1">
            <CloseIcon size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2">
                <SearchIcon size={16} className="text-[#6B635D]" />
              </div>
              <input
                type="text"
                placeholder="Search by email or source…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full p-2.5 pl-10 pr-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#D4A574] text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <div className="grid grid-cols-3 gap-2">
              {statusFilters.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setStatusFilter(f.id)}
                  className={`px-3 py-2 text-sm rounded border transition-colors ${
                    statusFilter === f.id
                      ? 'bg-[#D2A146] text-white border-[#D2A146]'
                      : 'bg-white text-[#1C2A46] border-[#DBDBDB] hover:border-[#D2A146]'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={() => {
              setSearchQuery('');
              setStatusFilter('all');
              onClose();
            }}
            className="flex-1 py-3 border border-gray-300 text-gray-700 font-medium rounded hover:bg-gray-50 transition-colors"
          >
            Clear All
          </button>
          <button
            onClick={onClose}
            className="flex-1 py-3 bg-[#D2A146] text-white font-medium rounded hover:bg-[#C49660] transition-colors"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </>
  );
};

/* ─────────── Mobile Card ─────────── */
const MobileSubscriberCard = ({ subscriber }) => {
  const cfg = adminNewsletterService.getStatusBadgeConfig(subscriber.status);
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-3">
      <div className="flex justify-between items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-gray-900 text-sm truncate">
            {subscriber.email}
          </div>
          <div className="text-xs text-gray-500 mt-0.5">
            via {subscriber.source || 'footer'}
          </div>
        </div>
        <span
          className={`flex items-center gap-1.5 px-2 py-1 text-xs font-semibold rounded-full flex-shrink-0 ${cfg.bgColor}`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${cfg.dotColor}`} />
          <span className={cfg.textColor}>{subscriber.status}</span>
        </span>
      </div>

      <div className="mt-3 flex justify-between text-xs text-gray-500">
        <span>Subscribed</span>
        <span>{adminNewsletterService.formatDate(subscriber.createdAt)}</span>
      </div>

      {subscriber.unsubscribedAt && (
        <div className="mt-1 flex justify-between text-xs text-gray-500">
          <span>Unsubscribed</span>
          <span>
            {adminNewsletterService.formatDate(subscriber.unsubscribedAt)}
          </span>
        </div>
      )}
    </div>
  );
};

/* ─────────── Main Tab ─────────── */
const NewsletterTab = () => {
  const { success, error: showError } = useToast();

  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const [stats, setStats] = useState({
    total: 0,
    subscribed: 0,
    unsubscribed: 0,
    last7Days: 0
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 20,
    totalPages: 1,
    totalItems: 0
  });

  const statusFilters = [
    { id: 'all', label: 'All' },
    { id: 'subscribed', label: 'Subscribed' },
    { id: 'unsubscribed', label: 'Unsubscribed' }
  ];

  /* ── Fetch list ── */
  const fetchSubscribers = useCallback(
    async (page = 1) => {
      try {
        setLoading(true);
        const res = await adminNewsletterService.getSubscribers({
          page,
          limit: pagination.itemsPerPage,
          search: searchQuery || undefined,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          sort: 'newest'
        });

        if (res.success) {
          setSubscribers(res.data.subscribers || []);
          setStats(res.data.stats || stats);
          setPagination((prev) => ({
            ...prev,
            currentPage: res.data.pagination.page,
            totalPages: res.data.pagination.pages,
            totalItems: res.data.pagination.total
          }));
        } else {
          setSubscribers([]);
        }
      } catch (err) {
        console.error('Fetch subscribers error:', err);
        showError('Failed to load subscribers');
        setSubscribers([]);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchQuery, statusFilter, pagination.itemsPerPage, showError]
  );

  useEffect(() => {
    fetchSubscribers(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Debounced filter changes ── */
  useEffect(() => {
    const t = setTimeout(() => {
      fetchSubscribers(1);
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, statusFilter]);

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
    fetchSubscribers(page);
  };

  /* ── Export ── */
  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      await adminNewsletterService.exportCsv({
        search: searchQuery || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined
      });
      success('CSV downloaded');
    } catch (err) {
      console.error('Export failed:', err);
      showError('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  /* ── Status badge (desktop) ── */
  const getStatusBadge = (status) => {
    const cfg = adminNewsletterService.getStatusBadgeConfig(status);
    return (
      <span
        className={`hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs leading-5 font-semibold rounded-full ${cfg.bgColor} w-fit`}
      >
        <span className={`h-2 w-2 rounded-full ${cfg.dotColor}`} />
        <span className={cfg.textColor}>{status}</span>
      </span>
    );
  };

  const statsCards = [
    {
      label: 'Total Subscribers',
      value: stats.total,
      icon: <EmailIcon color="#2F578F" />,
      color: 'bg-[#E7ECF4] text-[#2F578F]'
    },
    {
      label: 'Subscribed',
      value: stats.subscribed,
      icon: <CheckCircleIcon color="#2F8F5B" />,
      color: 'bg-[#E7F4ED] text-[#2F8F5B]'
    },
    {
      label: 'Unsubscribed',
      value: stats.unsubscribed,
      icon: <CautionIcon color="#A82121" />,
      color: 'bg-[#F4E7E7] text-[#A82121]'
    },
    {
      label: 'Last 7 Days',
      value: stats.last7Days,
      icon: <EmailIcon color="#8F7F2F" />,
      color: 'bg-[#F4F2E7] text-[#8F7F2F]'
    }
  ];

  return (
    <div className="px-2 sm:px-0">
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8">
        {statsCards.map((stat, i) => (
          <div key={i} className="bg-white rounded-sm p-3 sm:p-4 md:p-5 shadow-sm">
            <div
              className={`flex justify-center items-center rounded-md h-10 w-10 sm:h-12 sm:w-12 ${stat.color}`}
            >
              {stat.icon}
            </div>
            <p className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1C2A46] mt-2 sm:mt-4">
              {loading ? (
                <span className="inline-block w-6 h-6 bg-gray-200 rounded animate-pulse" />
              ) : (
                stat.value
              )}
            </p>
            <p className="text-xs sm:text-sm text-[#6B635D] mt-0.5 sm:mt-1">
              {stat.label}
            </p>
          </div>
        ))}
      </div>

      {/* List card */}
      <div className="bg-white rounded-sm shadow-sm">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-[#DBDBDB]">
          {/* Desktop */}
          <div className="hidden lg:flex items-center justify-between">
            <h2 className="text-lg sm:text-xl font-medium text-[#0E1628]">
              Newsletter Subscribers
            </h2>

            <div className="flex gap-3 lg:gap-4 items-center">
              {/* Search */}
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2">
                  <SearchIcon size={18} className="text-[#6B635D]" />
                </div>
                <input
                  type="text"
                  placeholder="Search email or source…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="p-2 pl-9 pr-3 border w-64 lg:w-80 border-[#DBDBDB] bg-transparent placeholder:text-[#6B635D] placeholder:text-xs lg:placeholder:text-sm focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent"
                />
              </div>

              {/* Status filter */}
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="p-2 pl-3 pr-7 border w-36 border-[#DBDBDB] bg-transparent text-[#1C2A46] text-xs lg:text-sm appearance-none focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent"
                >
                  {statusFilters.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.label}
                    </option>
                  ))}
                </select>
                <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                  <ArrowDown size={14} className="text-[#6B635D]" />
                </div>
              </div>

              {/* Export */}
              <button
                onClick={handleExport}
                disabled={exporting || loading}
                className="px-4 py-2 bg-[#D2A146] text-white text-xs lg:text-sm font-medium hover:bg-[#C49660] transition-colors disabled:opacity-60 disabled:cursor-wait"
              >
                {exporting ? 'Exporting…' : 'Export CSV'}
              </button>
            </div>
          </div>

          {/* Mobile */}
          <div className="lg:hidden">
            <div className="flex items-center justify-between mb-3 gap-3">
              <h2 className="text-base font-medium text-[#0E1628]">
                Newsletter Subscribers
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleExport}
                  disabled={exporting || loading}
                  className="px-3 py-2 bg-[#D2A146] text-white text-xs font-medium rounded-sm hover:bg-[#C49660] transition-colors disabled:opacity-60"
                >
                  {exporting ? 'Exporting…' : 'Export'}
                </button>
                <button
                  onClick={() => setMobileFiltersOpen(true)}
                  className="flex items-center gap-2 px-3 py-2 border border-[#DBDBDB] rounded-sm text-sm"
                >
                  <FilterIcon size={14} />
                  Filters
                </button>
              </div>
            </div>

            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2">
                <SearchIcon size={16} className="text-[#6B635D]" />
              </div>
              <input
                type="text"
                placeholder="Search email or source…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full p-2.5 pl-10 pr-3 border border-[#DBDBDB] bg-transparent placeholder:text-[#6B635D] placeholder:text-xs focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent rounded"
              />
            </div>
          </div>
        </div>

        {/* Desktop table */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="min-w-full divide-y divide-[#DBDBDB]">
            <thead className="bg-[#F3F4F6]">
              <tr>
                {['Email', 'Status', 'Subscribed'].map((h) => (
                  <th
                    key={h}
                    className="px-4 md:px-6 py-3 text-left text-xs font-normal text-[#6B635D] uppercase tracking-wider"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#DBDBDB]">
              {loading ? (
                <tr>
                  <td colSpan="5" className="text-center py-12">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#D2A146] mx-auto" />
                    <p className="mt-3 text-sm text-gray-600">Loading subscribers…</p>
                  </td>
                </tr>
              ) : subscribers.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-12 text-sm text-gray-500">
                    No subscribers found
                  </td>
                </tr>
              ) : (
                subscribers.map((s) => (
                  <tr
                    key={s._id}
                    className="hover:bg-gray-50 transition-colors duration-150 text-left"
                  >
                    <td className="px-4 md:px-6 py-3 md:py-4">
                      <span className="text-sm text-[#0E1628]">{s.email}</span>
                    </td>
                    <td className="px-4 md:px-6 py-3 md:py-4 whitespace-nowrap">
                      {getStatusBadge(s.status)}
                    </td>
                   
                    <td className="px-4 md:px-6 py-3 md:py-4 whitespace-nowrap">
                      <span className="text-sm text-[#8A9099]">
                        {adminNewsletterService.formatDate(s.createdAt)}
                      </span>
                    </td>
                   
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="lg:hidden px-4 pb-4">
          {loading ? (
            <div className="text-center py-10 text-sm text-gray-500">
              Loading subscribers…
            </div>
          ) : subscribers.length > 0 ? (
            subscribers.map((s) => (
              <MobileSubscriberCard key={s._id} subscriber={s} />
            ))
          ) : (
            <div className="text-center py-10 text-sm text-gray-500">
              No subscribers found
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && !loading && (
          <div className="border-t border-[#DBDBDB] px-4 sm:px-6 py-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="text-xs text-[#6B635D] order-2 sm:order-1">
                Showing {subscribers.length} out of {pagination.totalItems}{' '}
                subscribers
              </div>
              <div className="order-1 sm:order-2 w-full sm:w-auto">
                <Pagination
                  currentPage={pagination.currentPage}
                  totalPages={pagination.totalPages}
                  totalItems={pagination.totalItems}
                  itemsPerPage={pagination.itemsPerPage}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <MobileFiltersModal
        isOpen={mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        statusFilters={statusFilters}
      />
    </div>
  );
};

export default NewsletterTab;